import { Payload } from "@neworbit/redux-helpers";

import { loadAndTrack } from "redux-request-loading";

import { Dispatch } from "redux";

import { questionnaireLoaded } from "@teamprevent/common/questionnaires/actions";
import { QUESTIONNAIRE_KEY as employeeSectionQuestionnaireKey } from "@teamprevent/common/ppq/components/PpqEmployeeForm";
import { QUESTIONNAIRE_KEY as managerSectionQuestionnaireKey } from "@teamprevent/common/ppq/components/PpqManagerSection";
import { CaseType } from "@teamprevent/common/model";

import { AddTaskModel, CreateTaskModel, TaskType } from "../task/model";

import { HealthSurveillanceCaseCreateModel } from "../healthSurveillance/models";

import { ImmunisationCaseCreateModel } from "../crud/ohCase/model";

import { TriageApi } from "./triageApi";
import * as models from "./models";
import * as actions from "./actionTypes";

export type TriageAction =
    ({ type: typeof actions.LOAD_TRIAGES_SUCCESS } & Payload<models.TriageListModel[]>)
    | ({ type: typeof actions.LOAD_REFERRAL_TRIAGE_SUCCESS } & Payload<models.ReferralTriageDetailModel>)
    | ({ type: typeof actions.TRIAGE_REFERRAL_SUCCESS } & Payload<string>)
    | ({ type: typeof actions.REFERRAL_TRIAGE_TASKS_SUCCESS } & Payload<TaskType[]>)
    | ({ type: typeof actions.PPQ_TRIAGE_TASKS_SUCCESS } & Payload<TaskType[]>)
    | ({ type: typeof actions.LOAD_PPQ_TRIAGE_SUCCESS } & Payload<models.PpqTriageDetailModel>)
    | ({ type: typeof actions.PPQ_NO_FURTHER_ACTION_SUCCESS } & Payload<string>)
    | ({ type: typeof actions.PPQ_TEMPORARILY_UNFIT_SUCCESS } & Payload<string>)
    | ({ type: typeof actions.LOAD_PHYSIOTHERAPY_REFERRAL_TRIAGE_SUCCESS } & Payload<models.PhysiotherapyReferralTriageDetailModel>)
    | ({ type: typeof actions.TRIAGE_PHYSIOTHERAPY_REFERRAL_SUCCESS } & Payload<string>)
    | ({ type: typeof actions.PHYSIOTHERAPY_REFERRAL_TRIAGE_SUCCESSOR_TASKS_SUCCESS } & Payload<TaskType[]>)
    | ({ type: typeof actions.GENERIC_CASE_TRIAGE_SUCCESSOR_TASKS_SUCCESS } & Payload<TaskType[]>)
    | ({ type: typeof actions.LOAD_GENERIC_CASE_TRIAGE_SUCCESS } & Payload<models.GenericCaseTriageDetailModel>)
    | ({ type: typeof actions.TRIAGE_GENERIC_CASE_SUCCESS } & Payload<string>);

export const loadTriagesSuccess = (payload: models.TriageListModel[]): TriageAction => ({
    payload,
    type: actions.LOAD_TRIAGES_SUCCESS
});

export const loadReferralTriageSuccess = (payload: models.ReferralTriageDetailModel): TriageAction => ({
    payload,
    type: actions.LOAD_REFERRAL_TRIAGE_SUCCESS
});

export const loadPpqTriageSuccess = (payload: models.PpqTriageDetailModel): TriageAction => ({
    payload,
    type: actions.LOAD_PPQ_TRIAGE_SUCCESS
});

export const loadPhysiotherapyReferralTriageSuccess = (payload: models.PhysiotherapyReferralTriageDetailModel): TriageAction => ({
    payload,
    type: actions.LOAD_PHYSIOTHERAPY_REFERRAL_TRIAGE_SUCCESS
});

export const loadGenericCaseTriageSuccess = (payload: models.GenericCaseTriageDetailModel): TriageAction => ({
    payload,
    type: actions.LOAD_GENERIC_CASE_TRIAGE_SUCCESS
});

export const TriageReferralSuccess = (payload: string): TriageAction => ({
    payload,
    type: actions.TRIAGE_REFERRAL_SUCCESS
});

export const TriagePhysiotherapyReferralSuccess = (payload: string): TriageAction => ({
    payload,
    type: actions.TRIAGE_PHYSIOTHERAPY_REFERRAL_SUCCESS
});

export const TriageGenericCaseSuccess = (payload: string): TriageAction => ({
    payload,
    type: actions.TRIAGE_GENERIC_CASE_SUCCESS
});

export const loadReferralTriageTasksSuccess = (payload: TaskType[]): TriageAction => ({
    payload,
    type: actions.REFERRAL_TRIAGE_TASKS_SUCCESS
});

export const loadPpqTriageTasksSuccess = (payload: TaskType[]): TriageAction => ({
    payload,
    type: actions.PPQ_TRIAGE_TASKS_SUCCESS
});

export const loadPhysiotherapyReferralTriageSuccessorTasksSuccess = (payload: TaskType[]): TriageAction => ({
    payload,
    type: actions.PHYSIOTHERAPY_REFERRAL_TRIAGE_SUCCESSOR_TASKS_SUCCESS
});

export const loadGenericCaseTriageSuccessorTasksSuccess = (payload: TaskType[]): TriageAction => ({
    payload,
    type: actions.GENERIC_CASE_TRIAGE_SUCCESSOR_TASKS_SUCCESS
});

export const PpqNoFurtherActionSuccess = (payload: string): TriageAction => ({
    payload,
    type: actions.PPQ_NO_FURTHER_ACTION_SUCCESS
});

export const PpqTemporarilyUnfitSuccess = (payload: string): TriageAction => ({
    payload,
    type: actions.PPQ_TEMPORARILY_UNFIT_SUCCESS
});

export function loadReferralTriageTasks() {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, "loadReferralTriageTask", TriageApi.getReferralTriageTasks());
        dispatch(loadReferralTriageTasksSuccess(result));
    };
}

export function loadPpqTriageTasks() {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, "loadPpqTriageTask", TriageApi.getPpqTriageTasks());
        dispatch(loadPpqTriageTasksSuccess(result));
    };
}

export function loadPhysiotherapyReferralTriageSuccessorTasks() {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, "loadPhysiotherapyReferralTriageTasks", TriageApi.getPhysiotherapyReferralTriageSuccessorTasks());
        dispatch(loadPhysiotherapyReferralTriageSuccessorTasksSuccess(result));
    };
}

export function loadGenericCaseTriageSuccessorTasks() {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, "loadGenericCaseTriageTasks", TriageApi.getGenericCaseTriageSuccessorTasks());
        dispatch(loadGenericCaseTriageSuccessorTasksSuccess(result));
    };
}

export function loadTriages(customerIds: string[], caseTypes: CaseType[], teamIds: string[]) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, "loadTriages", TriageApi.get(customerIds, caseTypes, teamIds));
        dispatch(loadTriagesSuccess(result));
    };
}

export function loadReferralTriage(customerId: string, caseId: string) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.LOAD_REFERRAL_TRIAGE, TriageApi.getReferral(customerId, caseId));
        dispatch(loadReferralTriageSuccess(result));
    };
}

export function loadPpqTriage(customerId: string, caseId: string) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.LOAD_PPQ_TRIAGE, TriageApi.getPpq(customerId, caseId));
        dispatch(loadPpqTriageSuccess(result));
        dispatch(questionnaireLoaded({ questions: result.managersSection, questionnaireKey: managerSectionQuestionnaireKey }));
        dispatch(questionnaireLoaded({ questions: result.employeeSection, questionnaireKey: employeeSectionQuestionnaireKey }));
    };
}

export function loadPhysiotherapyReferralTriage(customerId: string, caseId: string) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.LOAD_PHYSIOTHERAPY_REFERRAL_TRIAGE, TriageApi.getPhysiotherapyReferral(customerId, caseId));
        dispatch(loadPhysiotherapyReferralTriageSuccess(result));
    };
}

export function loadGenericCaseTriage(customerId: string, caseId: string) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.LOAD_GENERIC_CASE_TRIAGE, TriageApi.getGenericCase(customerId, caseId));
        dispatch(loadGenericCaseTriageSuccess(result));
    };
}

/**
 * @deprecated Replaced by TriageReferralDetail when FeatureToggleNames.EnforceMandatoryNotesForInsufficientInfoTasks is toggled on
 */
export function triageReferralOld(customerId: string, ohCaseId: string, tasks: AddTaskModel[]) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.TRIAGE_REFERRAL, TriageApi.triageReferralOld(customerId, ohCaseId, tasks));
        if (result) {
            dispatch(TriageReferralSuccess(ohCaseId));
            return { success: true };
        }
        return { success: false };
    };
}

export function triageReferral(customerId: string, ohCaseId: string, tasks: CreateTaskModel[]) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.TRIAGE_REFERRAL, TriageApi.triageReferral(customerId, ohCaseId, tasks));
        if (result) {
            dispatch(TriageReferralSuccess(ohCaseId));
            return { success: true };
        }
        return { success: false };
    };
}

export function triagePhysiotherapyReferral(customerId: string, ohCaseId: string, tasks: AddTaskModel[]) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.TRIAGE_PHYSIOTHERAPY_REFERRAL, TriageApi.triagePhysiotherapyReferral(customerId, ohCaseId, tasks));
        if (result) {
            dispatch(TriagePhysiotherapyReferralSuccess(ohCaseId));
            return { success: true };
        }
        return { success: false };
    };
}

export function triageGenericCase(customerId: string, ohCaseId: string, tasks: CreateTaskModel[]) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.TRIAGE_GENERIC_CASE, TriageApi.triageGenericCase(customerId, ohCaseId, tasks));
        if (result) {
            dispatch(TriageGenericCaseSuccess(ohCaseId));
            return { success: true };
        }
        return { success: false };
    };
}

export function checkTriagePpqPossibility(
    triageId: string,
    caseId: string,
    customerId: string,
    employeeRecordId: string,
    createHealthSurveillanceCaseModel: HealthSurveillanceCaseCreateModel,
    createImmsCaseModel: ImmunisationCaseCreateModel,
    immsClearModel: models.ImmunisationsClearModel
) {
    const result = TriageApi.checkTriagePpqPossibility(
        triageId,
        caseId,
        customerId,
        employeeRecordId,
        createHealthSurveillanceCaseModel,
        createImmsCaseModel,
        immsClearModel
    );
    return result;
}

export function submitPpqNoFurtherAction(customerId: string, caseId: string, comments: string, outcome: string) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.PPQ_NO_FURTHER_ACTION, TriageApi.ppqNoFurtherAction(customerId, caseId, comments, outcome));
        if (result) {
            dispatch(PpqNoFurtherActionSuccess(caseId));
            return { success: true };
        }
        return { success: false };
    };
}

export function submitPpqTemporarilyUnfit(customerId: string, caseId: string, tasks: AddTaskModel[]) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.PPQ_TEMPORARILY_UNFIT, TriageApi.ppqTemporarilyUnfit(customerId, caseId, tasks));
        if (result) {
            dispatch(PpqTemporarilyUnfitSuccess(caseId));
            return { success: true };
        }
        return { success: false };
    };
}

export function submitPpqTemporarilyUnfitPendingFitForWork(customerId: string, caseId: string, comments: string) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(
            dispatch,
            actions.PPQ_TEMPORARILY_UNFIT,
            TriageApi.ppqTemporarilyUnfitPendingFitForWork(customerId, caseId, comments)
        );
        if (result) {
            dispatch(PpqTemporarilyUnfitSuccess(caseId));
            return { success: true };
        }
        return { success: false };
    };
}
